import { inject } from '@angular/core';
import { Router, CanActivateFn } from '@angular/router';
import { Store } from '@ngxs/store';
import { AuthState } from 'src/app/store/auth';

/**
 * Blume Admin Guard
 *
 * Protects routes by ensuring that the authenticated user's email address
 * ends with '@blumemethod.com'. This guard should be used in combination with
 * authGuard to ensure the user is authenticated first.
 *
 * This guard:
 * 1. Retrieves the user's email from the auth state
 * 2. Checks if the email ends with '@blumemethod.com' (case-insensitive)
 * 3. Redirects to /account if the user is not a Blume admin
 * 4. Allows access if the user is a Blume admin
 *
 * @example
 * // In app.routes.ts
 * {
 *   path: 'blume-admin',
 *   canActivate: [authGuard, emailVerifiedGuard, blumeAdminGuard],
 *   loadComponent: () => import('./blume-admin.component')
 * }
 *
 * @returns true if user is Blume admin, false otherwise (with redirect)
 */
export const blumeAdminGuard: CanActivateFn = () => {
  const store = inject(Store);
  const router = inject(Router);

  console.log('[BlumeAdminGuard] Checking Blume admin access');

  // Get the user's email from auth state
  const userEmail = store.selectSnapshot(AuthState.userEmail);

  console.log('[BlumeAdminGuard] User email:', userEmail);

  // Check if email exists and ends with @blumemethod.com (case-insensitive)
  if (!userEmail) {
    console.log('[BlumeAdminGuard] No email found, redirecting to account');
    router.navigate(['/account']);
    return false;
  }

  const isBlumeAdmin = userEmail.toLowerCase().endsWith('@blumemethod.com');

  if (!isBlumeAdmin) {
    console.log(
      '[BlumeAdminGuard] User is not a Blume admin, redirecting to account'
    );
    router.navigate(['/account']);
    return false;
  }

  // User is a Blume admin, allow access
  console.log('[BlumeAdminGuard] User is Blume admin, allowing access');
  return true;
};

